<!--
  Card shell matches the other detail boxes on the record (white, 1px border,
  12px radius) rather than the flat inset strip this used to be.
-->
<div class="eoi-ext-links" *ngIf="currentLink || loadError">
  <div class="eoi-ext-links__head">
    <i class="fa-solid fa-link"></i>
    <span>{{ heading }}</span>
  </div>

  <!--
    Shown instead of silently rendering as "no link" — a request that failed (a dropped
    connection, the API mid-deploy) must not look identical to a record that never had a link
    generated for it, which is exactly what "I created one and now it isn't there" was.
  -->
  <div class="eoi-ext-links__error" *ngIf="loadError">
    <i class="fa-solid fa-triangle-exclamation"></i>
    <span>Couldn't load this record's external EOI link{{ links.length ? 's' : '' }}.</span>
    <button type="button" (click)="reload()">Retry</button>
  </div>

  <div class="eoi-ext-link" *ngIf="currentLink as link">
    <!--
      One row: the raw token URL was 90 characters of noise in a read-only input, so these are two
      labelled actions instead. Buyer name is left out — this card only ever sits on that buyer's
      own record, where the name is already on screen. The status badge is suppressed for
      "Link Generated": the card only existing already says that, and the label was pure repetition.
    -->
    <div class="eoi-ext-link__row">
      <div class="eoi-ext-link__actions">
        <button type="button" class="eoi-ext-link__btn" (click)="copy(link)" title="Copy the buyer's link">
          <i class="fa-regular fa-copy"></i> Copy Link
        </button>
        <button type="button" class="eoi-ext-link__btn" (click)="open(link)" title="Open the buyer form">
          <i class="fa-solid fa-arrow-up-right-from-square"></i> View Link
        </button>
        <!-- Once the buyer has submitted, the EOI their answers produced is the
             useful destination — the form itself is locked by then. -->
        <button
          type="button"
          class="eoi-ext-link__btn"
          *ngIf="canOpenEoi(link)"
          (click)="openEoi(link)"
          [title]="isSubmitted(link) ? 'Open the EOI this form created' : 'Open the linked EOI'">
          <img [src]="eoiIcon" class="eoi-ext-link__btn-icon" alt="" /> View EOI
        </button>
        <!-- Reissuing or deactivating a link the buyer has already submitted doesn't mean
             anything — the form it pointed at is done and locked. Offering them here read as
             live controls sitting on a completed record; if a link ever needs reopening after
             submission that belongs in the record's history, not as a button next to it. -->
        <button
          type="button"
          class="eoi-ext-link__btn eoi-ext-link__btn--icon"
          (click)="regenerate(link)"
          title="Issue a new link"
          *ngIf="canManage && !isSubmitted(link)">
          <i class="fa-solid fa-rotate"></i>
        </button>
        <button
          type="button"
          class="eoi-ext-link__btn eoi-ext-link__btn--icon is-danger"
          (click)="deactivate(link)"
          title="Deactivate link"
          *ngIf="canManage && link.status !== 'cancelled' && !isSubmitted(link)">
          <i class="fa-solid fa-ban"></i>
        </button>
        <!-- Only once a link is already deactivated: delete removes it for good, so it does not
             have to be kept around just because it is no longer usable. -->
        <button
          type="button"
          class="eoi-ext-link__btn eoi-ext-link__btn--icon is-danger"
          (click)="delete(link)"
          title="Delete link permanently"
          *ngIf="canManage && link.status === 'cancelled'">
          <i class="fa-solid fa-trash-can"></i>
        </button>
      </div>

      <!--
        Two lines rather than one run-on row: who's responsible and when is one fact, the
        lot/status/expiry is another, and cramming all five onto one line was wrapping mid-sentence
        on anything narrower than a wide desktop.
      -->
      <div class="eoi-ext-link__meta">
        <div class="eoi-ext-link__meta-row">
          <span *ngIf="link.lot_number">Lot {{ link.lot_number }}</span>
          <!-- The agent responsible for the lead/EOI, not whoever happened to click "Generate" —
               falls back to the generator's name only when the host hasn't passed one in. -->
          <span *ngIf="agentName">Agent {{ agentName }}</span>
          <span *ngIf="!agentName && link.generated_by_name">Sent by {{ link.generated_by_name }}</span>
          <span *ngIf="link.generated_at">{{ link.generated_at | date: 'd MMM y, h:mm a' }}</span>
        </div>
        <div class="eoi-ext-link__meta-row">
          <span class="eoi-ext-link__badge" [ngClass]="statusMeta[link.status]?.badge" *ngIf="link.status !== 'link_generated'">
            {{ statusMeta[link.status]?.label || link.status }}
          </span>
          <span *ngIf="link.submitted_at">Submitted {{ link.submitted_at | date: 'd MMM y, h:mm a' }}</span>
          <span *ngIf="link.expires_at">Expires {{ link.expires_at | date: 'd MMM y' }}</span>
        </div>
      </div>
    </div>
  </div>
</div>

<!--
  Inline "View Link" — renders the buyer's form through the authenticated view endpoint (no OTP
  gate) using the same `<app-external-eoi-form>` component the project's Preview screen uses.
-->
<div class="eoi-ext-view-backdrop" *ngIf="viewLoading || viewConfig" (click)="closeView()">
  <div class="eoi-ext-view" (click)="$event.stopPropagation()">
    <button type="button" class="eoi-ext-view__close" (click)="closeView()"><i class="fa-solid fa-xmark"></i></button>
    <div class="eoi-ext-view__loading" *ngIf="viewLoading && !viewConfig">
      <div class="eoi-ext-view__spinner"></div>
      <p>Loading…</p>
    </div>
    <!-- `draftLinkId` turns on the agent's "Save for buyer" button: whatever the agent types here is
         saved against the link as draft answers, so the buyer's real form opens already filled in. -->
    <app-external-eoi-form
      *ngIf="viewConfig"
      [previewConfig]="viewConfig"
      [draftLinkId]="canManage ? viewingLink?._id || null : null"
      (draftSaved)="reload()"></app-external-eoi-form>
  </div>
</div>
